import React from 'react';
import { safeFormat } from '@/lib/safeDate';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  format,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { CalendarEvent } from '@/hooks/useCalendarEvents';
import type { AgendaItem } from '@/hooks/useAgendaEvents';

type GridEvent = CalendarEvent | AgendaItem;

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const TYPE_STYLES: Record<string, string> = {
  meeting: 'bg-blue-100 text-blue-700',
  reminder: 'bg-amber-100 text-amber-700',
  other: 'bg-purple-100 text-purple-700',
  unavailable: 'bg-gray-200 text-gray-600',
};

function getEventStyle(event: GridEvent) {
  if (event.color) return { style: { backgroundColor: event.color + '22', color: event.color } };
  return { className: TYPE_STYLES[event.type] ?? TYPE_STYLES.other };
}

interface CalendarGridProps {
  year: number;
  month: number; // 1-based
  events: GridEvent[];
  /** Click on empty cell area → create event */
  onDayClick: (date: Date) => void;
  /** Click on day number or "+N mais" → open day view */
  onDayLabelClick?: (date: Date) => void;
  onEventClick: (event: GridEvent) => void;
}

export function CalendarGrid({ year, month, events, onDayClick, onDayLabelClick, onEventClick }: CalendarGridProps) {
  const monthDate = new Date(year, month - 1, 1);
  const gridStart = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const eventsByDay = React.useMemo(() => {
    const map = new Map<string, GridEvent[]>();
    for (const ev of events) {
      const key = safeFormat(new Date(ev.start_at), 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    return map;
  }, [events]);

  return (
    <div className="flex flex-col gap-0 border border-border rounded-lg overflow-auto bg-background">
      {/* Week day headers */}
      <div className="grid grid-cols-7 border-b border-border">
        {WEEK_DAYS.map((d) => (
          <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground">
            {d}
          </div>
        ))}
      </div>

      {/* Day cells — rows of 7 */}
      <div className="grid grid-cols-7 auto-rows-[minmax(100px,1fr)]">
        {days.map((day) => {
          const key = safeFormat(day, 'yyyy-MM-dd');
          const dayEvents: GridEvent[] = eventsByDay.get(key) ?? [];
          const isCurrentMonth = isSameMonth(day, monthDate);
          const todayDay = isToday(day);
          const MAX_VISIBLE = 3;
          const visibleEvents = dayEvents.slice(0, MAX_VISIBLE);
          const overflow = dayEvents.length - MAX_VISIBLE;

          return (
            <div
              key={key}
              className={cn(
                'min-h-[120px] p-1.5 border-b border-r border-border last:border-r-0 cursor-pointer',
                'hover:bg-accent/30 transition-colors',
                !isCurrentMonth && 'bg-muted/20',
              )}
              onClick={() => onDayClick(day)}
            >
              {/* Day number — click goes to day view */}
              <div className="flex justify-start mb-1">
                <span
                  className={cn(
                    'text-sm w-6 h-6 flex items-center justify-center rounded-full',
                    onDayLabelClick && 'cursor-pointer hover:ring-2 hover:ring-primary/40',
                    todayDay
                      ? 'bg-primary text-primary-foreground font-bold'
                      : isCurrentMonth
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                  )}
                  onClick={(e) => {
                    if (onDayLabelClick) {
                      e.stopPropagation();
                      onDayLabelClick(day);
                    }
                  }}
                >
                  {safeFormat(day, 'd')}
                </span>
              </div>

              {/* Event chips */}
              <div className="flex flex-col gap-0.5">
                {visibleEvents.map((ev) => {
                  const { className, style } = getEventStyle(ev) as any;
                  const isCanceled = (ev as any).status === 'canceled';
                  const isCompleted = (ev as any).status === 'completed';
                  return (
                    <button
                      key={ev.id}
                      className={cn(
                        'w-full text-left text-xs rounded px-1.5 py-0.5 truncate leading-4',
                        className,
                        isCanceled && 'opacity-50 line-through',
                        isCompleted && 'opacity-50 line-through',
                      )}
                      style={style}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(ev);
                      }}
                    >
                      {!ev.all_day && (
                        <span className="opacity-70 mr-1">
                          {safeFormat(new Date(ev.start_at), 'HH:mm')}
                        </span>
                      )}
                      {ev.title}
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <button
                    className="text-xs text-muted-foreground pl-1 hover:text-foreground text-left"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onDayLabelClick) onDayLabelClick(day);
                    }}
                  >
                    ⋯ +{overflow} mais
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
